import { useEffect, useState } from 'react';
import { api } from '../api';
import { useI18n } from '../i18n';
import { EmptyState, ErrorState, Field, Modal, Skeleton, Spinner } from '../components';
import { IconPlus } from '../icons';
import { describe } from '../errors';

type Item = {
  medicineText: string;
  dose: string;
  route: string;
  frequency: string;
  duration: string;
  quantity?: string;
  instructions?: string;
};

type Detail = Awaited<ReturnType<typeof api.prescription>>;
type Template = Awaited<ReturnType<typeof api.rxTemplates>>['templates'][number];

const BLANK: Item = { medicineText: '', dose: '', route: 'oral', frequency: '', duration: '' };

/**
 * Prescribing, inside the patient's file.
 *
 * Writing a prescription is something you do WHILE looking at a patient — their
 * allergies, what you have just treated, what they are already taking. Sending
 * someone to a separate top-level section to do it, and asking them to pick the
 * patient again once they arrive, gets that exactly backwards. The clinic-wide
 * list is still useful for "what did we issue this week"; it is not where a
 * prescription should be born.
 *
 * The state machine is unchanged and still the safety control:
 *
 *   draft ──check──► checked ──issue──► issued (immutable)
 *                └─► blocked
 *
 * What changed is that BLOCKED is no longer a dead end. It used to be
 * unreachable in both directions — not editable, not removable — so the only
 * way out was to write a second prescription and leave the first sitting in the
 * record looking live. A draft or a blocked prescription can now be rewritten
 * (which voids its verdict and forces a fresh check) or discarded outright.
 */
export function PrescribePanel({
  patientId,
  patientName,
  canIssue,
}: {
  patientId: string;
  patientName: string;
  canIssue: boolean;
}) {
  const { t, formatDate } = useI18n();
  const [rows, setRows] = useState<Awaited<ReturnType<typeof api.prescriptions>>['prescriptions'] | null>(null);
  const [mode, setMode] = useState<Awaited<ReturnType<typeof api.rxMode>> | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [writing, setWriting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    setRows(null);
    api
      .prescriptions(patientId)
      .then((r) => setRows(r.prescriptions))
      .catch((e) => setError(describe(e, t)));
    api.rxMode().then(setMode).catch(() => setMode(null));
  }, [patientId, nonce, t]);

  if (error) return <ErrorState message={error} onRetry={() => setNonce((n) => n + 1)} />;

  return (
    <>
      {/* The clinical dataset in use is stated on every prescribing screen, not
          buried in settings. It changes what a "passed" check is worth. */}
      {mode?.warning ? (
        <div className="notice warn" style={{ marginBottom: 16 }}>
          <span>{mode.warning}</span>
        </div>
      ) : null}

      <div className="toolbar">
        <div className="toolbar-spacer" />
        <button type="button" className="btn" onClick={() => setWriting(true)}>
          <IconPlus size={16} />
          {t.rx.new}
        </button>
      </div>

      <div className="card">
        <div className="card-head">
          <h2>
            {t.rx.title}
            {rows ? <span className="count-chip">{rows.length}</span> : null}
          </h2>
        </div>
        {rows === null ? (
          <Skeleton rows={3} />
        ) : rows.length === 0 ? (
          <EmptyState title={t.rx.none} />
        ) : (
          <div className="table-scroll">
            <table>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="clickable" onClick={() => setOpenId(r.id)}>
                    <td>
                      <strong>
                        {r.itemCount} {t.rx.items}
                      </strong>
                      <span className="sub">{r.prescriber ?? '—'}</span>
                    </td>
                    <td>{formatDate(r.issuedAt ?? r.createdAt)}</td>
                    <td>
                      <span className={`badge ${stateTone(r.state)}`}>
                        {(t.rx.states as Record<string, string>)[r.state] ?? r.state}
                      </span>
                    </td>
                    <td style={{ textAlign: 'end' }}>
                      <button
                        type="button"
                        className="btn-link"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenId(r.id);
                        }}
                      >
                        {t.patients.view}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {writing ? (
        <Composer
          patientId={patientId}
          patientName={patientName}
          onClose={() => setWriting(false)}
          onCreated={(id) => {
            setWriting(false);
            setNonce((n) => n + 1);
            setOpenId(id);
          }}
        />
      ) : null}

      {openId ? (
        <RxDetail
          id={openId}
          canIssue={canIssue}
          onClose={() => setOpenId(null)}
          onChanged={() => setNonce((n) => n + 1)}
        />
      ) : null}
    </>
  );
}

function stateTone(state: string): string {
  if (state === 'issued') return 'ok';
  if (state === 'blocked') return 'danger';
  if (state === 'checked') return 'info';
  if (state === 'cancelled') return '';
  return 'warn';
}

/**
 * Writing one.
 *
 * A template fills the items in and is then FORGOTTEN — the prescription is
 * whatever is on screen when it is saved. Carrying the template's identity
 * forward would invite the idea that its previous safety verdict travels with
 * it, and it never does: every prescription is checked against this patient.
 */
function Composer({
  patientId,
  patientName,
  onClose,
  onCreated,
}: {
  patientId: string;
  patientName: string;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const { t, locale } = useI18n();
  const [items, setItems] = useState<Item[]>([{ ...BLANK }]);
  const [notes, setNotes] = useState('');
  const [templates, setTemplates] = useState<Template[]>([]);
  const [saveAs, setSaveAs] = useState('');
  const [saveScope, setSaveScope] = useState<'personal' | 'clinic'>('personal');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.rxTemplates().then((r) => setTemplates(r.templates)).catch(() => setTemplates([]));
  }, []);

  const complete = items.every(
    (i) => i.medicineText.trim() && i.dose.trim() && i.frequency.trim() && i.duration.trim(),
  );

  function setItem(index: number, patch: Partial<Item>) {
    setItems((list) => list.map((it, i) => (i === index ? { ...it, ...patch } : it)));
  }

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      if (saveAs.trim()) {
        // Saving the habit is a separate act from writing this prescription,
        // and failing to save a template must not lose the prescription.
        await api
          .saveTemplate({ nameEn: saveAs.trim(), items, scope: saveScope })
          .catch(() => undefined);
      }
      const r = await api.createPrescription({ patientId, items, notes: notes.trim() || undefined });
      onCreated(r.id);
    } catch (e) {
      setError(describe(e, t));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={`${t.rx.new} — ${patientName}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            {t.patients.cancel}
          </button>
          <button type="button" className="btn" onClick={() => void submit()} disabled={busy || !complete}>
            {busy ? <Spinner /> : t.rx.saveDraft}
          </button>
        </>
      }
    >
      <div className="stack">
        {error ? (
          <div className="notice danger" role="alert">
            {error}
          </div>
        ) : null}

        {templates.length > 0 ? (
          <Field label={t.rx.template} hint={t.rx.templateHint}>
            <select
              value=""
              onChange={(e) => {
                const chosen = templates.find((x) => x.id === e.target.value);
                if (chosen) setItems((chosen.items as Item[]).map((i) => ({ ...BLANK, ...i })));
              }}
            >
              <option value="">—</option>
              {templates.map((x) => (
                <option key={x.id} value={x.id}>
                  {locale === 'ar' ? x.nameAr : x.nameEn}
                  {x.scope === 'personal' ? ` · ${t.rx.mine}` : ''}
                </option>
              ))}
            </select>
          </Field>
        ) : null}

        {items.map((item, index) => (
          <div key={index} className="card" style={{ padding: 14 }}>
            <div className="row" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
              <span className="section-label" style={{ margin: 0 }}>
                {t.rx.medicine} {index + 1}
              </span>
              {items.length > 1 ? (
                <button
                  type="button"
                  className="btn-link danger"
                  onClick={() => setItems((list) => list.filter((_, i) => i !== index))}
                >
                  {t.common.close}
                </button>
              ) : null}
            </div>
            <div className="stack" style={{ gap: 10 }}>
              <Field label={t.rx.medicine} required>
                <input
                  value={item.medicineText}
                  onChange={(e) => setItem(index, { medicineText: e.target.value })}
                  autoFocus={index === 0}
                />
              </Field>
              <div className="grid-2">
                <Field label={t.rx.dose} required>
                  <input value={item.dose} onChange={(e) => setItem(index, { dose: e.target.value })} />
                </Field>
                <Field label={t.rx.route} required>
                  <input value={item.route} onChange={(e) => setItem(index, { route: e.target.value })} />
                </Field>
                <Field label={t.rx.frequency} required>
                  <input value={item.frequency} onChange={(e) => setItem(index, { frequency: e.target.value })} />
                </Field>
                <Field label={t.rx.duration} required>
                  <input value={item.duration} onChange={(e) => setItem(index, { duration: e.target.value })} />
                </Field>
              </div>
              <Field label={t.rx.instructions} hint={t.patients.optional}>
                <input
                  value={item.instructions ?? ''}
                  onChange={(e) => setItem(index, { instructions: e.target.value })}
                />
              </Field>
            </div>
          </div>
        ))}

        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => setItems((list) => [...list, { ...BLANK }])}
          disabled={items.length >= 20}
        >
          <IconPlus size={15} />
          {t.rx.addItem}
        </button>

        <Field label={t.patients.notes} hint={t.patients.optional}>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
        </Field>

        {/* Saving what you just wrote as a template, from where you wrote it —
            the moment you know it is worth keeping. */}
        <div className="card" style={{ padding: 14 }}>
          <Field label={t.rx.saveAsTemplate} hint={t.rx.saveAsTemplateHint}>
            <input value={saveAs} onChange={(e) => setSaveAs(e.target.value)} />
          </Field>
          {saveAs.trim() ? (
            <div className="segmented" style={{ marginTop: 10 }}>
              <button type="button" aria-pressed={saveScope === 'personal'} onClick={() => setSaveScope('personal')}>
                {t.rx.mine}
              </button>
              <button type="button" aria-pressed={saveScope === 'clinic'} onClick={() => setSaveScope('clinic')}>
                {t.rx.clinicWide}
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}

/** One prescription: its verdict, and what can still be done to it. */
export function RxDetail({
  id,
  canIssue,
  onClose,
  onChanged,
}: {
  id: string;
  canIssue: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { t } = useI18n();
  const [data, setData] = useState<Detail | null>(null);
  const [editing, setEditing] = useState(false);
  const [items, setItems] = useState<Item[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    api
      .prescription(id)
      .then((d) => {
        setData(d);
        // Nullable on the wire, optional here. Every nullable field, not
        // just one — a null that slips through is refused on save.
        setItems(
          d.items.map((i) => ({
            ...BLANK,
            ...i,
            quantity: i.quantity ?? undefined,
            instructions: i.instructions ?? undefined,
          })),
        );
      })
      .catch((e) => setError(describe(e, t)));
  }, [id, nonce, t]);

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      setNonce((n) => n + 1);
      onChanged();
    } catch (e) {
      setError(describe(e, t));
    } finally {
      setBusy(false);
    }
  }

  const state = data?.prescription.state;
  const editable = state === 'draft' || state === 'blocked';

  return (
    <Modal
      title={t.rx.title}
      onClose={onClose}
      footer={
        !data ? null : (
          <>
            {editable ? (
              <button
                type="button"
                className="btn btn-danger"
                disabled={busy}
                onClick={() =>
                  void run(() => api.discardPrescription(id, 'Discarded before issue'))
                }
              >
                {t.rx.discard}
              </button>
            ) : null}
            {editable && !editing ? (
              <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => setEditing(true)}>
                {t.rx.edit}
              </button>
            ) : null}
            {editing ? (
              <button
                type="button"
                className="btn"
                disabled={busy}
                onClick={() =>
                  void run(async () => {
                    await api.updatePrescription(id, { items });
                    setEditing(false);
                  })
                }
              >
                {busy ? <Spinner /> : t.chart.save}
              </button>
            ) : state === 'draft' || state === 'blocked' ? (
              <button type="button" className="btn" disabled={busy} onClick={() => void run(() => api.checkPrescription(id))}>
                {busy ? <Spinner /> : t.rx.check}
              </button>
            ) : state === 'checked' && canIssue && data.issuable.allowed ? (
              <button type="button" className="btn" disabled={busy} onClick={() => void run(() => api.issuePrescription(id))}>
                {busy ? <Spinner /> : t.rx.issue}
              </button>
            ) : null}
          </>
        )
      }
    >
      {!data ? (
        <Skeleton rows={5} />
      ) : (
        <div className="stack">
          {error ? (
            <div className="notice danger" role="alert">
              {error}
            </div>
          ) : null}

          <div className="row">
            <span className={`badge ${stateTone(data.prescription.state)}`}>
              {(t.rx.states as Record<string, string>)[data.prescription.state] ?? data.prescription.state}
            </span>
            {data.check?.stale ? <span className="badge warn">{t.rx.stale}</span> : null}
          </div>

          {/* A blocked prescription now says what to do about it, rather than
              leaving the reader at a dead end. */}
          {data.prescription.state === 'blocked' ? (
            <div className="notice danger">
              <div>
                <strong>{t.rx.blockedTitle}</strong>
                <div style={{ marginTop: 4 }}>{t.rx.blockedHelp}</div>
              </div>
            </div>
          ) : null}

          {data.check ? (
            <div>
              <div className="section-label">{t.rx.findings}</div>
              {data.check.findings.length === 0 ? (
                <p style={{ color: 'var(--ink-3)', marginTop: 6 }}>{t.rx.noFindings}</p>
              ) : (
                <div className="stack" style={{ gap: 8, marginTop: 8 }}>
                  {data.check.findings.map((f, i) => (
                    <div key={i} className={f.severity === 'critical' ? 'notice danger' : 'notice warn'}>
                      <span>{f.message}</span>
                    </div>
                  ))}
                </div>
              )}
              {data.check.isSynthetic ? (
                <p className="hint" style={{ color: 'var(--ink-3)', fontSize: 12, marginTop: 8 }}>
                  {t.rx.syntheticNote}
                </p>
              ) : null}
            </div>
          ) : null}

          <div>
            <div className="section-label">{t.rx.items}</div>
            {editing ? (
              <div className="stack" style={{ gap: 10, marginTop: 8 }}>
                {items.map((item, index) => (
                  <div key={index} className="card" style={{ padding: 12 }}>
                    <Field label={t.rx.medicine} required>
                      <input
                        value={item.medicineText}
                        onChange={(e) =>
                          setItems((l) => l.map((x, i) => (i === index ? { ...x, medicineText: e.target.value } : x)))
                        }
                      />
                    </Field>
                    {/* Every field the item has, not a subset. The editor
                        used to show three of six, so the duration or the route
                        could be wrong and there was no way to correct it here. */}
                    <div className="grid-2" style={{ marginTop: 10 }}>
                      <Field label={t.rx.dose} required>
                        <input
                          value={item.dose}
                          onChange={(e) =>
                            setItems((l) => l.map((x, i) => (i === index ? { ...x, dose: e.target.value } : x)))
                          }
                        />
                      </Field>
                      <Field label={t.rx.route} required>
                        <input
                          value={item.route}
                          onChange={(e) =>
                            setItems((l) => l.map((x, i) => (i === index ? { ...x, route: e.target.value } : x)))
                          }
                        />
                      </Field>
                      <Field label={t.rx.frequency} required>
                        <input
                          value={item.frequency}
                          onChange={(e) =>
                            setItems((l) => l.map((x, i) => (i === index ? { ...x, frequency: e.target.value } : x)))
                          }
                        />
                      </Field>
                      <Field label={t.rx.duration} required>
                        <input
                          value={item.duration}
                          onChange={(e) =>
                            setItems((l) => l.map((x, i) => (i === index ? { ...x, duration: e.target.value } : x)))
                          }
                        />
                      </Field>
                      <Field label={t.rx.quantity} hint={t.patients.optional}>
                        <input
                          value={item.quantity ?? ''}
                          onChange={(e) =>
                            setItems((l) =>
                              l.map((x, i) => (i === index ? { ...x, quantity: e.target.value || undefined } : x)),
                            )
                          }
                        />
                      </Field>
                      <Field label={t.rx.instructions} hint={t.patients.optional}>
                        <input
                          value={item.instructions ?? ''}
                          onChange={(e) =>
                            setItems((l) =>
                              l.map((x, i) =>
                                i === index ? { ...x, instructions: e.target.value || undefined } : x,
                              ),
                            )
                          }
                        />
                      </Field>
                    </div>
                    {items.length > 1 ? (
                      <button
                        type="button"
                        className="btn-link danger"
                        style={{ marginTop: 10 }}
                        onClick={() => setItems((l) => l.filter((_, i) => i !== index))}
                      >
                        {t.rx.removeItem}
                      </button>
                    ) : null}
                  </div>
                ))}
                <div className="notice">
                  <span>{t.rx.editVoidsCheck}</span>
                </div>
              </div>
            ) : (
              <div className="table-scroll" style={{ marginTop: 8 }}>
                <table>
                  <tbody>
                    {data.items.map((i) => (
                      <tr key={i.id}>
                        <td style={{ paddingInlineStart: 0 }}>
                          <strong>{i.medicineText}</strong>
                          <span className="sub">
                            {i.dose} · {i.frequency} · {i.duration}
                          </span>
                        </td>
                        <td style={{ paddingInlineEnd: 0, textAlign: 'end' }}>
                          {i.ingredients.length === 0 ? (
                            <span className="badge warn">{t.rx.unresolved}</span>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {!data.issuable.allowed && data.issuable.reason ? (
            <p style={{ color: 'var(--ink-3)', fontSize: 12.5, margin: 0 }}>{data.issuable.reason}</p>
          ) : null}
        </div>
      )}
    </Modal>
  );
}

